import { useState } from "react";
import { register } from "../../../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../../../hooks/useUser";
import { useToast } from "@chakra-ui/react";
import { track, identify, EVENTS } from "../../../utils/analytics";
import { trackMetaEvent, META_EVENTS } from "../../../utils/metaPixel";
import { getAttribution, generateEventId } from "../../../utils/attribution";

interface RegisterController {
  email: string;
  password: string;
  formPasswordError: string;
  formEmailError: string;
  errorMessage: string;
  loading: boolean;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  register: () => void;
}

export const useRegisterController = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [formEmailError, setFormEmailError] = useState<string>("");
  const [formPasswordError, setFormPasswordError] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();
  const user = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referCodeFromQuery = searchParams.get("refcode") || "";

  const performRegister = async () => {
    track(EVENTS.AUTH.REGISTER_ATTEMPT, {
      email: email,
      referralCode: referCodeFromQuery,
    });

    setLoading(true);

    // Generate event ID for Meta deduplication
    const metaEventId = generateEventId();

    // Get attribution data
    const attribution = getAttribution();

    setTimeout(() => {
      register(email, password, metaEventId, attribution)
        .then((res) => {
          if (res.success === true) {
            track(EVENTS.AUTH.REGISTER_SUCCESS, {
              email: email,
            });

            // Track Meta Pixel CompleteRegistration event
            trackMetaEvent(
              META_EVENTS.COMPLETE_REGISTRATION,
              {
                content_name: "User Registration",
                status: "completed",
              },
              metaEventId
            );

            user.setLoginSettings(
              res.user.email,
              res.user.gridTheme,
              res.token
            );
            identify(res.user.email);

            setTimeout(() => {
              navigate("/app/dashboard");
            }, 200);
          } else {
            track(EVENTS.AUTH.REGISTER_FAILURE, {
              email: email,
              error: "Form validation failed",
            });

            console.log("Registration error", res);
            if (res.errors) {
              res.errors.filter((e: any) => {
                if (e.path === "email") {
                  setFormEmailError(e.msg);
                  toast({
                    title: "Error",
                    description: e.msg,
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                  });
                }
                if (e.path === "password") {
                  setFormPasswordError(e.msg);
                  toast({
                    title: "Error",
                    description: e.msg,
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                  });
                }
              });
            } else {
              track(EVENTS.AUTH.REGISTER_FAILURE, {
                email: email,
                error: res.message,
              });

              toast({
                title: "Error",
                description: res.message,
                status: "error",
                duration: 4000,
                isClosable: true,
              });
            }
            setLoading(false);
          }
        })
        .catch((error) => {
          console.log("Register error", error);
          setLoading(false);
        });
    }, 200);
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    errorMessage,
    loading,
    formEmailError,
    formPasswordError,
    register: performRegister,
  } as RegisterController;
};
